import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";

export type KnowledgeBase = {
  id: string;
  name: string;
  description?: string;
  document_count: number;
  chunk_count: number;
  created_at?: string;
  updated_at?: string;
};

export type KnowledgeBasePayload = {
  name: string;
  description?: string;
};

export type Document = {
  id: string;
  knowledge_base: string;
  title: string;
  content?: string;
  content_type?: string;
  source_url?: string | null;
  chunk_count?: number;
  created_at?: string;
  updated_at?: string;
};

export type DocumentChunk = {
  id: string;
  chunk_index: number;
  content: string;
  has_embedding: boolean;
  document?: string;
  document_title?: string;
  similarity?: number;
};

export type DocumentPayload = {
  knowledge_base: string;
  title: string;
  content: string;
  source_url?: string;
  content_type?: string;
};

export const documentsApi = {
  list: async (kbId: string) => {
    const res = await api.get<Paginated<Document> | Document[]>(
      "/knowledge-bases/documents/",
      { params: { knowledge_base: kbId } },
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },

  get: async (id: string) => {
    const res = await api.get<Document>(`/knowledge-bases/documents/${id}/`);
    return res.data;
  },

  create: async (data: DocumentPayload) => {
    const res = await api.post<Document>("/knowledge-bases/documents/", data);
    return res.data;
  },

  upload: async (formData: FormData) => {
    const res = await api.post<Document>(
      "/knowledge-bases/documents/upload/",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data;
  },

  update: async (id: string, data: Partial<DocumentPayload>) => {
    const res = await api.patch<Document>(
      `/knowledge-bases/documents/${id}/`,
      data,
    );
    return res.data;
  },

  remove: async (id: string) => {
    await api.delete(`/knowledge-bases/documents/${id}/`);
  },

  chunks: async (id: string) => {
    const res = await api.get<DocumentChunk[]>(
      `/knowledge-bases/documents/${id}/chunks/`,
    );
    return res.data;
  },

  search: async (params: {
    query: string;
    knowledge_base: string;
    top_k?: number;
  }) => {
    const res = await api.post<{ results: DocumentChunk[] }>(
      "/knowledge-bases/documents/search/",
      { top_k: 5, ...params },
    );
    return res.data.results;
  },
};

export const knowledgeBasesApi = {
  list: async () => {
    const res = await api.get<Paginated<KnowledgeBase> | KnowledgeBase[]>(
      "/knowledge-bases/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },

  get: async (id: string) => {
    const res = await api.get<KnowledgeBase>(`/knowledge-bases/${id}/`);
    return res.data;
  },

  create: async (data: KnowledgeBasePayload) => {
    const res = await api.post<KnowledgeBase>("/knowledge-bases/", data);
    return res.data;
  },

  update: async (id: string, data: Partial<KnowledgeBasePayload>) => {
    const res = await api.patch<KnowledgeBase>(`/knowledge-bases/${id}/`, data);
    return res.data;
  },

  remove: async (id: string) => {
    await api.delete(`/knowledge-bases/${id}/`);
  },
};
